import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { endOfDay, startOfDay } from 'date-fns';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { User } from 'lucide-react';
import { ChipList, DataGrid, DataGridSortModel, FilterDateRange, FilterSearch, Spinner } from '@repo/components';
import GenericDataPageHeader from 'src/components/GenericDataPageHeader';
import { PERMISSIONS } from '@repo/shared/domain';
import { ProjectForm } from '@repo/forms';
import { EventEmitter, getVerifyPermission, sortToString, stringToSort } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useLocalStorage } from '@repo/components/hooks';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getProjects, createProject, updateProject } from 'src/api/personal/projects';
import { getPrincipalGroupLabelsSettings } from 'src/api/settings';

interface SearchParams {
	tab?: string;
	sort?: string;
	page?: number;
	zoneIds?: string;
	groupIds?: string;
	search?: string;
	from?: string;
	until?: string;
}

export const Route = createFileRoute('/personal_/projects/')({
	validateSearch: (search: Record<string, unknown>): SearchParams => ({
		tab: (search.tab as string) || '0',
		sort: (search.sort as string) || 'name:asc',
		page: (search.page as number) || 0,
		zoneIds: search.zoneIds as string,
		groupIds: search.groupIds as string,
		search: search.search as string,
		from: search.from as string,
		until: search.until as string,
	}),
	component: ProjectsPage,
});

function ProjectsPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const search = useSearch({ from: '/personal_/projects/' });
	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const { updateValue } = useLocalStorage<string>('personalDashboardProjectsBackUrl');
	const [isFormOpen, setIsFormOpen] = useState(false);

	useBreadcrumbLevel(0, 'Personal Dashboard', '/personal');
	useBreadcrumbLevel(1, 'Projects');

	const hasProjectsPermission = verifyPermission([PERMISSIONS.personalProjectsView]);

	useEffect(() => {
		if (!authLoading && !hasProjectsPermission) {
			navigate({ to: '/personal/overview' });
		}
	}, [authLoading, hasProjectsPermission, navigate]);

	const queryParams = useMemo(() => {
		const params: Record<string, string> = {
			tab: search.tab ?? '0',
			sort: search.sort || 'name:asc',
		};
		if (search.page) params.page = search.page;
		if (search.zoneIds) params.zoneIds = search.zoneIds;
		if (search.groupIds) params.groupIds = search.groupIds;
		if (search.search) params.search = search.search;
		if (search.from) params.from = search.from;
		if (search.until) params.until = search.until;
		return params;
	}, [search]);

	const {
		data: projectsData,
		isLoading: projectsLoading,
		refetch: refetchProjects,
	} = useQuery({
		queryKey: ['personal-projects', queryParams],
		queryFn: () => getProjects(queryParams),
	});

	const { data: principalGroupLabelsSettings } = useQuery({
		queryKey: ['principal-group-labels-settings'],
		queryFn: () => getPrincipalGroupLabelsSettings(),
	});

	const principalGroupLabels = useMemo(
		() => principalGroupLabelsSettings?.settings?.principalGroupLabels?.map((item) => item.value) || [],
		[principalGroupLabelsSettings],
	);

	const updateQueryParams = useCallback(
		(params: Partial<SearchParams>) => {
			navigate({
				to: '/personal/projects',
				search: (prev) => ({ ...prev, ...params }),
			});
		},
		[navigate],
	);

	const handleSort = ([sort]: DataGridSortModel) => {
		if (sort) {
			updateQueryParams({ sort: sortToString(sort) });
		}
	};

	const handleRowClick = ({ row }: { row: { id: string } }) => {
		updateValue(typeof window !== 'undefined' ? window.location.href : '');
		navigate({ to: `/personal/projects/${row.id}` });
	};

	const handleSearchChange = debounce((searchValue: string) => {
		updateQueryParams({ search: searchValue || undefined });
	}, 350);

	const handleDateFilterChange = (dateFilter: { from?: string; until?: string }) => {
		updateQueryParams(dateFilter);
	};

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page: page });
	};

	const handleListTabChange = (tab: number) => {
		updateQueryParams({ tab: tab.toString() });
	};

	const projectTabs = useMemo(
		() => [
			{ id: '0', label: 'All' },
			{ id: '1', label: 'Archived' },
		],
		[],
	);

	const actions = useMemo(() => {
		return verifyPermission([PERMISSIONS.personalProjectsCreate])
			? [
					<Button onClick={() => setIsFormOpen(true)} startIcon={<AddIcon />} variant="outlined" key="add">
						Add Project
					</Button>,
				]
			: [];
	}, [verifyPermission]);

	const handleCloseForm = () => {
		setIsFormOpen(false);
	};

	const handleSubmit = async (data: (Partial<{ id: string }> & Record<string, unknown>) | undefined) => {
		let isError = false;
		let id: string | undefined;
		if (data) {
			try {
				if (data.id) {
					await updateProject(data.id, data);
				} else {
					const result = await createProject(data);
					id = result.id;
				}
				EventEmitter.emit('toast', { type: 'success', message: 'Project saved' });
				queryClient.invalidateQueries({ queryKey: ['personal-projects'] });
				refetchProjects();
				setIsFormOpen(false);
				if (id) {
					updateValue(typeof window !== 'undefined' ? window.location.href : '');
					navigate({ to: `/personal/projects/${id}` });
				}
			} catch (error: unknown) {
				isError = true;
				const err = error as { message?: string; body?: { message?: string } };
				const errorMessage =
					err?.message || err?.body?.message || 'Failed to save Project. Did you enter all data correctly?';
				EventEmitter.emit('toast', { type: 'error', message: errorMessage });
			}
		}
		if (!isError) {
			setIsFormOpen(false);
		}
	};

	const defaultProjectData = {
		startDate: startOfDay(new Date()),
		endDate: endOfDay(new Date()),
		adminIds: authenticatedUser?.id ? [authenticatedUser.id] : [],
	};

	const columns = [
		{
			field: 'name',
			headerName: 'Name',
		},
		{
			field: 'startDate',
			headerName: 'Start Date',
			valueFormatter: (params: { value: string | null }) =>
				params.value ? new Date(params.value).toLocaleDateString('en-GB') : '',
		},
		{
			field: 'endDate',
			headerName: 'End Date',
			valueFormatter: (params: { value: string | null }) =>
				params.value ? new Date(params.value).toLocaleDateString('en-GB') : '',
		},
		{
			field: 'principalGroupsPopulated',
			headerName: 'Responsible Companies',
			renderCell: (c: { value: Array<{ id: string; name: string }> | null }) => {
				if (!c.value || !Array.isArray(c.value)) return null;
				const uniqueGroups = c.value.filter((group, index, self) => index === self.findIndex((g) => g.id === group.id));
				return <ChipList data={uniqueGroups} getLabel={(group) => group.name} />;
			},
		},
		{
			field: 'zones',
			headerName: 'Zones',
			flex: 1,
			renderCell: (c: { value: Array<{ name?: string }> | null }) => (
				<ChipList data={c.value} getLabel={(o) => o?.name} />
			),
			sortable: false,
		},
		{
			field: 'admins',
			headerName: 'Contacts',
			flex: 1,
			renderCell: (c: {
				value: Array<{ firstName?: string; lastName?: string; email?: string; status?: string }> | null;
			}) => (
				<ChipList
					data={c.value?.map((user) => ({ ...user, status: undefined }))}
					getLabel={(o) => (o?.lastName ? `${o?.firstName} ${o?.lastName}` : o?.email)}
					icon={<User size={14} className="text-gray-500" />}
				/>
			),
			sortable: false,
		},
		{
			field: 'updatedAt',
			headerName: 'Updated',
			valueFormatter: (params: { value: string | null }) =>
				params.value ? new Date(params.value).toLocaleDateString('en-GB') : '',
		},
	];

	const activeTab = parseInt(search.tab || '0', 10);
	const isLoading = authLoading;

	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	return (
		<>
			<div className="px-4 flex justify-center">
				<div className="max-w-[1280px] w-full">
					<div className="-mx-4">
						<GenericDataPageHeader
							title="Projects"
							tabs={projectTabs}
							defaultTab="0"
							onTabChange={handleListTabChange}
							actions={actions}
						/>
					</div>
					<div className="mt-2 flex flex-row gap-2 flex-wrap">
						<div className="w-3/4 min-w-0 flex-1">
							<FilterSearch
								label="Search Project"
								defaultValue={search.search || ''}
								onChange={(searchInput: string) => handleSearchChange(searchInput)}
								sx={{ width: '100%' }}
							/>
						</div>
						{activeTab === 0 && (
							<div className="w-1/4 min-w-[200px]">
								<FilterDateRange
									label="Filter Date Range"
									value={search.from || search.until ? { from: search.from, until: search.until } : undefined}
									onChange={handleDateFilterChange}
									sx={{ width: '100%' }}
								/>
							</div>
						)}
					</div>

					<Paper variant="outlined" sx={{ marginTop: 2 }}>
						<DataGrid
							data={projectsData?.data || []}
							columns={columns}
							rowCount={projectsData?.count || 0}
							paginationMode="server"
							sortingMode="server"
							onPaginationModelChange={handlePaginationModelChange}
							onSortModelChange={handleSort}
							isLoading={projectsLoading}
							sortingOrder={['asc', 'desc']}
							sortModel={[stringToSort((search.sort || 'name:asc') as `${string}:${string}`)]}
							pageSize={projectsData?.pageSize || 25}
							pageSizeOptions={[projectsData?.pageSize || 25]}
							page={projectsData?.page ?? 0}
							onRowClick={handleRowClick}
							pagination
							onReload={() => {
								refetchProjects();
								queryClient.invalidateQueries({ queryKey: ['personal-projects'] });
							}}
						/>
					</Paper>
				</div>
			</div>
			<ProjectForm
				isOpen={isFormOpen}
				onClose={handleCloseForm}
				onSubmit={handleSubmit}
				data={defaultProjectData}
				principalGroupLabels={principalGroupLabels}
			/>
		</>
	);
}
